import { clinicianFormsFor } from './clinicianName.js'
// caseDeclined.js — TEST 3.18, the patient half (patient 242.235.0).
//
// ★★★ WHAT HAPPENS ON THE SERVER (orella-api D3, POST /api/v1/cases/<id>/decline). A physician
//   (or an MA for him) declines a case that was SENT to him and that he does not hold yet. The
//   case goes back to `ready_to_share` with no target physician, no holder and no appointment
//   request, and the patient gets ONE notification of type `case_declined` whose data carries
//   { doctorName, caseId, declineReason, declineReasonEs }. The reason is one of the three
//   sentences Alex approved (2026-10-02, Q9), in English and Spanish. The physician's free-text
//   note is never sent to the patient, so this file never looks for one.
//
// ★★ THE LIVE API (D2) HAS NO DECLINE. No `case_declined` row exists there, so nothing here runs;
//   and a row without the two reason fields (an older or partial payload) reads without its
//   reason line rather than with an empty one.
//
// ★ The copy is Alex's Q91 notice (title, "not able to take your case", "Open Orella to choose
//   another physician") with the reason line he asked for, rendered in the app language. The
//   server's own title and message are English only; this replaces them for this one type.

export const CASE_DECLINED_TYPE = 'case_declined'

/** Is this notification a physician declining the patient's case? (dot-notation tolerated) */
export function isCaseDeclined(notification) {
  const type = typeof notification === 'string' ? notification : notification?.type
  return typeof type === 'string' && type.replace(/\./g, '_') === CASE_DECLINED_TYPE
}

// ★ PROD-729: THE UNDO. From orella-api Release A on (PROD-657, A3), the physician who declined a
//   case can undo it within 7 days: the case goes back to `shared` with him, and the patient gets
//   ONE `case_decline_undone` notification whose data is { doctorName, caseId }. Its copy is an
//   English draft on the server, so the row reads the server's words in English and the generic
//   fallback under es (NotificationsScreen rowCopy); it opens the case home after the same store
//   refresh a decline gets, because the cached case is the released one.
export const CASE_DECLINE_UNDONE_TYPE = 'case_decline_undone'

/** Did the physician undo his decline of the patient's case? (dot-notation tolerated) */
export function isCaseDeclineUndone(notification) {
  const type = typeof notification === 'string' ? notification : notification?.type
  return typeof type === 'string' && type.replace(/\./g, '_') === CASE_DECLINE_UNDONE_TYPE
}

const text = (v) => (typeof v === 'string' ? v.trim() : '')

/**
 * The approved reason sentence in the patient's language: Spanish when the app is in Spanish
 * and the API sent the Spanish sentence, otherwise the English one. '' when the API sent none.
 */
export function declineReasonFor(data, language) {
  if (!data || typeof data !== 'object') return ''
  const en = text(data.declineReason)
  const es = text(data.declineReasonEs)
  if (language === 'es' && es) return es
  return en
}

/**
 * Title and message for a case_declined row, in the app language.
 * @param {object} notification - the row from GET /notifications
 * @param {string} language - the app language code
 * @param {(key: string, params?: object) => string} t - the i18n translator
 */
export function caseDeclinedCopy(notification, language, t) {
  const data = (notification && typeof notification.data === 'object' && notification.data) || {}
  const doctorName = text(data.doctorName).replace(/^Dr\.?\s+/i, '')
  const reason = declineReasonFor(data, language)
  const parts = [
    doctorName
      ? t('notifications.caseDeclined.notAble', { ...clinicianFormsFor(data.doctorTitle, doctorName, 'display', language), doctorName })
      : t('notifications.caseDeclined.notAbleNoName'),
  ]
  if (reason) parts.push(t('notifications.caseDeclined.reason', { reason }))
  parts.push(t('notifications.caseDeclined.chooseAnother'))
  return { title: t('notifications.caseDeclined.title'), message: parts.join(' ') }
}
